/**
 * WebSocket Client Logger
 * 
 * Provides configurable logging for WebSocket client operations.
 * Only logs in development mode to avoid polluting production console.
 * 
 * Validates: Requirement 12.3, 12.5
 * - THE WebSocket_Client SHALL log connection state changes and subscription events to the browser console in development mode
 * - THE system SHALL provide configurable log levels (debug, info, warn, error)
 */

/**
 * Log levels in order of severity
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

/**
 * Log level names for display
 */
const LOG_LEVEL_NAMES: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: 'DEBUG',
  [LogLevel.INFO]: 'INFO',
  [LogLevel.WARN]: 'WARN',
  [LogLevel.ERROR]: 'ERROR',
  [LogLevel.NONE]: 'NONE'
};

/**
 * Log level colors for console styling
 */
const LOG_LEVEL_COLORS: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: '#9CA3AF', // gray
  [LogLevel.INFO]: '#3B82F6',  // blue
  [LogLevel.WARN]: '#F59E0B',  // amber
  [LogLevel.ERROR]: '#EF4444', // red
  [LogLevel.NONE]: '#000000'
};

/**
 * Log level icons for visual distinction
 */
const LOG_LEVEL_ICONS: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: '🔍',
  [LogLevel.INFO]: 'ℹ️',
  [LogLevel.WARN]: '⚠️',
  [LogLevel.ERROR]: '❌',
  [LogLevel.NONE]: ''
};

/**
 * Logger configuration
 */
export interface LoggerConfig {
  /** Minimum log level to display */
  level: LogLevel;
  /** Whether logging is enabled */
  enabled: boolean;
  /** Prefix for all log messages */
  prefix: string;
  /** Whether to include timestamps */
  includeTimestamp: boolean;
  /** Whether to use styled console output */
  useStyles: boolean;
}

/**
 * Log entry structure
 */
export interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: Date;
  category?: string;
}

/**
 * WebSocket Logger class
 * 
 * Provides structured logging for WebSocket client operations.
 * Automatically disabled in production mode.
 */
export class WebSocketLogger {
  private config: LoggerConfig;
  private isDevelopment: boolean;

  constructor(config?: Partial<LoggerConfig>) {
    // Check if we're in development mode
    this.isDevelopment = this.checkDevelopmentMode();

    this.config = {
      level: config?.level ?? LogLevel.DEBUG,
      enabled: config?.enabled ?? this.isDevelopment,
      prefix: config?.prefix ?? '[WebSocket]',
      includeTimestamp: config?.includeTimestamp ?? true,
      useStyles: config?.useStyles ?? true
    };
  }

  /**
   * Check if running in development mode
   */
  private checkDevelopmentMode(): boolean {
    // Check Vite environment variable
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env.DEV === true || import.meta.env.MODE === 'development';
    }

    // Fallback to process.env for Node.js environments
    if (typeof process !== 'undefined' && process.env) {
      return process.env.NODE_ENV === 'development';
    }

    // Default to false (production) if we can't determine
    return false;
  }

  /**
   * Check if a log level should be displayed
   */
  private shouldLog(level: LogLevel): boolean {
    return this.config.enabled && this.isDevelopment && level >= this.config.level;
  }

  /**
   * Format timestamp for log output
   */
  private formatTimestamp(): string {
    const now = new Date();
    return now.toISOString().split('T')[1].slice(0, -1); // HH:mm:ss.sss
  }

  /**
   * Create styled log message
   */
  private createStyledMessage(level: LogLevel, category: string, message: string): string[] {
    const icon = LOG_LEVEL_ICONS[level];
    const color = LOG_LEVEL_COLORS[level];
    const levelName = LOG_LEVEL_NAMES[level];
    const timestamp = this.config.includeTimestamp ? `[${this.formatTimestamp()}]` : '';

    if (this.config.useStyles) {
      return [
        `%c${this.config.prefix}%c ${timestamp} %c${levelName}%c ${icon} [${category}] ${message}`,
        'color: #8B5CF6; font-weight: bold;', // prefix style (purple)
        'color: #6B7280;', // timestamp style (gray)
        `color: ${color}; font-weight: bold;`, // level style
        'color: inherit;' // message style
      ];
    }

    return [`${this.config.prefix} ${timestamp} ${levelName} ${icon} [${category}] ${message}`];
  }

  /**
   * Log a message at the specified level
   */
  private log(level: LogLevel, category: string, message: string, data?: any): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const styledMessage = this.createStyledMessage(level, category, message);

    switch (level) {
      case LogLevel.DEBUG:
        if (data !== undefined) {
          console.debug(...styledMessage, data);
        } else {
          console.debug(...styledMessage);
        }
        break;
      case LogLevel.INFO:
        if (data !== undefined) {
          console.info(...styledMessage, data);
        } else {
          console.info(...styledMessage);
        }
        break;
      case LogLevel.WARN:
        if (data !== undefined) {
          console.warn(...styledMessage, data);
        } else {
          console.warn(...styledMessage);
        }
        break;
      case LogLevel.ERROR:
        if (data !== undefined) {
          console.error(...styledMessage, data);
        } else {
          console.error(...styledMessage);
        }
        break;
    }
  }

  // ============================================
  // Public Logging Methods
  // ============================================

  /**
   * Log debug message
   */
  debug(category: string, message: string, data?: any): void {
    this.log(LogLevel.DEBUG, category, message, data);
  }

  /**
   * Log info message
   */
  info(category: string, message: string, data?: any): void {
    this.log(LogLevel.INFO, category, message, data);
  }

  /**
   * Log warning message
   */
  warn(category: string, message: string, data?: any): void {
    this.log(LogLevel.WARN, category, message, data);
  }

  /**
   * Log error message
   */
  error(category: string, message: string, data?: any): void {
    this.log(LogLevel.ERROR, category, message, data);
  }

  // ============================================
  // Connection State Logging Methods
  // ============================================

  /**
   * Log connection state change
   */
  logConnectionStateChange(oldState: string, newState: string, details?: any): void {
    this.info('Connection', `State changed: ${oldState} → ${newState}`, details);
  }

  /**
   * Log connection established
   */
  logConnected(socketId: string, details?: any): void {
    this.info('Connection', `Connected successfully`, { socketId, ...details });
  }

  /**
   * Log disconnection
   */
  logDisconnected(reason: string, details?: any): void {
    this.warn('Connection', `Disconnected: ${reason}`, details);
  }

  /**
   * Log reconnection attempt
   */
  logReconnecting(attemptNumber: number, details?: any): void {
    this.info('Connection', `Reconnecting... (attempt ${attemptNumber})`, details);
  }

  /**
   * Log reconnection success
   */
  logReconnected(attemptNumber: number, details?: any): void {
    this.info('Connection', `Reconnected after ${attemptNumber} attempts`, details);
  }

  /**
   * Log reconnection failure
   */
  logReconnectFailed(details?: any): void {
    this.error('Connection', 'Reconnection failed', details);
  }

  /**
   * Log connection error
   */
  logConnectionError(error: Error, details?: any): void {
    this.error('Connection', `Connection error: ${error.message}`, { error, ...details });
  }

  // ============================================
  // Subscription Logging Methods
  // ============================================

  /**
   * Log subscription created
   */
  logSubscribed(subscription: any): void {
    this.info('Subscription', 'Subscribed', subscription);
  }

  /**
   * Log subscription removed
   */
  logUnsubscribed(taskId: string): void {
    this.info('Subscription', `Unsubscribed from task: ${taskId}`);
  }

  /**
   * Log subscription error
   */
  logSubscriptionError(subscription: any, error: string): void {
    this.error('Subscription', `Subscription failed: ${error}`, subscription);
  }

  /**
   * Log subscriptions restored
   */
  logSubscriptionsRestored(count: number): void {
    this.info('Subscription', `Restored ${count} subscriptions`);
  }

  // ============================================
  // Message Logging Methods
  // ============================================

  /**
   * Log message received
   */
  logMessageReceived(messageType: string, message: any): void {
    this.debug('Message', `Received: ${messageType}`, message);
  }

  /**
   * Log message sent
   */
  logMessageSent(event: string, data: any): void {
    this.debug('Message', `Sent: ${event}`, data);
  }

  /**
   * Log message handler error
   */
  logMessageHandlerError(messageType: string, error: Error): void {
    this.error('Message', `Handler error for ${messageType}: ${error.message}`, { error });
  }

  /**
   * Log message queued
   */
  logMessageQueued(event: string, queueSize: number): void {
    this.debug('Message', `Queued: ${event} (queue size: ${queueSize})`);
  }

  /**
   * Log message queue flushed
   */
  logMessageQueueFlushed(count: number): void {
    this.info('Message', `Flushed ${count} queued messages`);
  }

  // ============================================
  // Heartbeat Logging Methods
  // ============================================

  /**
   * Log heartbeat ping received
   */
  logHeartbeatPing(): void {
    this.debug('Heartbeat', 'Ping received, sending pong');
  }

  /**
   * Log heartbeat timeout
   */
  logHeartbeatTimeout(timeSinceLastMessage: number): void {
    this.warn('Heartbeat', `Timeout detected (${timeSinceLastMessage}ms since last message)`);
  }

  // ============================================
  // Fallback Mode Logging Methods
  // ============================================

  /**
   * Log fallback mode activated
   */
  logFallbackActivated(): void {
    this.warn('Fallback', 'Fallback mode activated (switching to polling)');
  }

  /**
   * Log fallback mode deactivated
   */
  logFallbackDeactivated(): void {
    this.info('Fallback', 'Fallback mode deactivated (WebSocket restored)');
  }

  /**
   * Log mode transition
   */
  logModeTransition(from: string, to: string): void {
    this.info('Fallback', `Mode transition: ${from} → ${to}`);
  }

  // ============================================
  // Configuration Methods
  // ============================================

  /**
   * Update logger configuration
   */
  setConfig(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
    this.debug('Logger', 'Configuration updated', this.config);
  }

  /**
   * Set log level
   */
  setLevel(level: LogLevel): void {
    this.config.level = level;
    this.debug('Logger', `Log level set to ${LOG_LEVEL_NAMES[level]}`);
  }

  /**
   * Enable logging
   */
  enable(): void {
    this.config.enabled = true;
  }

  /**
   * Disable logging
   */
  disable(): void {
    this.config.enabled = false;
  }

  /**
   * Check if logging is enabled
   */
  isEnabled(): boolean {
    return this.config.enabled && this.isDevelopment;
  }

  /**
   * Get current configuration
   */
  getConfig(): LoggerConfig {
    return { ...this.config };
  }
}

// ============================================
// Singleton Instance
// ============================================

/**
 * Default logger instance
 * Use this for most logging needs
 */
export const wsLogger = new WebSocketLogger();

/**
 * Create a new logger instance with custom configuration
 */
export function createLogger(config?: Partial<LoggerConfig>): WebSocketLogger {
  return new WebSocketLogger(config);
}
